/**
 * Apply small UPS list edits from a Request Detail edit session on Save Changes (#996).
 * Mirrors rackEditSessionFlush. Note PUT (not PATCH) for unit updates, and the
 * reorder body key is `smallUpsIds`.
 */

import { api } from './api';
import {
  computeRepeatableOperations,
  type ComputedRepeatableOps,
} from './computeRepeatableOperations';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { flushRepeatableInstanceAttachmentSectionRemaps } from './repeatableInstanceAttachmentRemapFlush';
import { deleteRepeatableUnitTolerating404 } from './repeatableUnitDelete';
import { mirrorSmallUpsToOfflineRequestCache } from './smallUpsOfflineSync';
import { toCreateSmallUpsPayload } from './smallUpsPayload';
import {
  LEGACY_SMALL_UPS_SYNTHETIC_ID,
  validateSessionSmallUpsNames,
} from '../components/smallups/smallUpsListUtils';
import { SMALL_UPS_REPEATABLE_ATTACHMENT_BASE_SECTIONS } from '../utils/repeatableInstanceAttachmentSections';

import type { SmallUpsConfiguration } from '../types/smallUps';

function isPersistedSmallUpsId(id: number | undefined): id is number {
  return id != null && id > 0;
}

function isSessionOnlySmallUpsId(id: number | undefined): boolean {
  return id == null || id <= 0 || id === LEGACY_SMALL_UPS_SYNTHETIC_ID;
}

/**
 * Canonicalize for content comparison: sort keys and drop undefined values so
 * two rows with identical CONTENT compare equal regardless of key insertion
 * order. Load-bearing since #997 Story 6.4 (mirror of `rackEditSessionFlush`):
 * the engine save bridge's baseline (raw API/mapper rows, identity keys first)
 * and the session rows (round-tripped through `descriptorInstanceToSmallUps`,
 * identity keys appended last) are different object lineages — a raw
 * `JSON.stringify` flagged every unit as changed, PUTting the whole list on save.
 */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (value && typeof value === 'object') {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const entry = (value as Record<string, unknown>)[key];
      if (entry !== undefined) {
        sorted[key] = canonicalize(entry);
      }
    }
    return sorted;
  }
  return value;
}

function smallUpsContentKey(unit: SmallUpsConfiguration): string {
  // `requestId` excluded too (#997 Story 6.4, mirror rack review): the engine
  // conversion stamps `parseInt('o-…') → 0` on offline-request session rows,
  // which would keep every untouched unit permanently "changed" against the
  // cached baseline.
  const {
    id: _id,
    requestId: _rid,
    createdAt: _ca,
    updatedAt: _ua,
    ...rest
  } = unit;
  return JSON.stringify(canonicalize(rest));
}

function smallUpsEqual(
  a: SmallUpsConfiguration[],
  b: SmallUpsConfiguration[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(
    (unit, index) => smallUpsContentKey(unit) === smallUpsContentKey(b[index]),
  );
}

export function smallUpsEditSessionHasChanges(
  baseline: SmallUpsConfiguration[],
  session: SmallUpsConfiguration[],
): boolean {
  return !smallUpsEqual(baseline, session);
}

/**
 * Pure save-time diff for small UPS units (#1501): consumed by this flush AND
 * by the aggregate save payload builder — one diff implementation, two
 * consumers.
 */
export function computeSmallUpsOperations(
  baseline: SmallUpsConfiguration[],
  session: SmallUpsConfiguration[],
): ComputedRepeatableOps<SmallUpsConfiguration> {
  return computeRepeatableOperations(baseline, session, {
    isPersistedId: isPersistedSmallUpsId,
    isSessionOnlyId: isSessionOnlySmallUpsId,
    contentKey: smallUpsContentKey,
    stripForUpdate: unit => {
      const {
        id: _id,
        createdAt: _ca,
        updatedAt: _ua,
        requestId: _rid,
        ...data
      } = unit;
      return data;
    },
  });
}

export async function flushSmallUpsEditSession(
  requestId: string,
  baseline: SmallUpsConfiguration[],
  session: SmallUpsConfiguration[],
  isOfflineRequest: boolean,
): Promise<SmallUpsConfiguration[]> {
  if (!smallUpsEditSessionHasChanges(baseline, session)) {
    return session;
  }

  const nameError = validateSessionSmallUpsNames(session);
  if (nameError) {
    throw new Error(nameError);
  }

  const idKey = String(requestId);
  const useOnlineApi = networkService.isConnected() && !isOfflineRequest;

  const ops = computeSmallUpsOperations(baseline, session);

  for (const deletedId of ops.deletes) {
    if (useOnlineApi) {
      await deleteRepeatableUnitTolerating404(
        `/requests/${idKey}/small-ups/${deletedId}`,
      );
    } else {
      await offlineCache.addSmallUpsToOfflineQueue(
        idKey,
        'delete',
        undefined,
        deletedId,
      );
    }
  }

  const createdIdMap = new Map<number, number>();

  for (const { tempId, row } of ops.creates) {
    const payload = toCreateSmallUpsPayload({
      ...row,
      requestId: parseInt(idKey, 10) || row.requestId,
    });
    if (useOnlineApi) {
      const created = await api.post<SmallUpsConfiguration>(
        `/requests/${idKey}/small-ups`,
        payload,
      );
      if (tempId != null && created.id) {
        createdIdMap.set(tempId, created.id);
      }
    } else {
      await offlineCache.addSmallUpsToOfflineQueue(
        idKey,
        'create',
        payload,
        tempId,
      );
    }
  }

  for (const { id, data } of ops.updates) {
    if (useOnlineApi) {
      await api.put<SmallUpsConfiguration>(
        `/requests/${idKey}/small-ups/${id}`,
        data,
      );
    } else {
      await offlineCache.addSmallUpsToOfflineQueue(idKey, 'update', data, id);
    }
  }

  const tempIdByClientKey = new Map(
    ops.creates.map(c => [c.clientKey, c.tempId]),
  );
  const orderedPersistedIds = ops.orderRefs
    .map(ref => {
      if (typeof ref === 'number') {
        return ref;
      }
      const tempId = tempIdByClientKey.get(ref);
      return tempId != null && createdIdMap.has(tempId)
        ? createdIdMap.get(tempId)!
        : null;
    })
    .filter((id): id is number => id != null);

  if (
    orderedPersistedIds.length > 0 &&
    JSON.stringify(orderedPersistedIds) !==
      JSON.stringify(ops.baselineOrderInSession)
  ) {
    if (useOnlineApi) {
      await api.patch<SmallUpsConfiguration[]>(
        `/requests/${idKey}/small-ups/reorder`,
        { smallUpsIds: orderedPersistedIds },
      );
    } else {
      await offlineCache.addSmallUpsToOfflineQueue(
        idKey,
        'reorder',
        undefined,
        undefined,
        orderedPersistedIds,
      );
    }
  }

  await flushRepeatableInstanceAttachmentSectionRemaps(
    idKey,
    createdIdMap,
    SMALL_UPS_REPEATABLE_ATTACHMENT_BASE_SECTIONS,
    useOnlineApi,
  );

  const normalizedSession = session.map(unit => {
    if (unit.id != null && createdIdMap.has(unit.id)) {
      return { ...unit, id: createdIdMap.get(unit.id)! };
    }
    return unit;
  });

  if (!useOnlineApi) {
    await offlineCache.updateCachedRequestSmallUps(idKey, normalizedSession);
  } else {
    await mirrorSmallUpsToOfflineRequestCache(idKey, normalizedSession);
  }

  return normalizedSession;
}
